import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Link } from "react-router-dom";
import {
  Flame, Bell, Mail, HandHelping, Headphones, TrendingUp, Calendar,
  AlertTriangle, ArrowRight, Users,
} from "lucide-react";
import { PageHeader, fmtUSD, fmtDateTime } from "../../components/admin/ui";
import SyncHealthBadge from "../../components/admin/SyncHealthBadge";

const StatCard = ({ label, value, sub, icon: Icon, accent = "#C9A44C", testid }) => (
  <div className="bento-card !p-6" data-testid={testid}>
    <span className="glow-blob" />
    <div className="flex items-start justify-between">
      <div className="icon-badge"><Icon className="h-5 w-5" style={{ color: accent }} strokeWidth={1.5} /></div>
      <span className="text-[10px] tracking-[0.22em] uppercase text-[#C9A44C]/70">{label}</span>
    </div>
    <div className="mt-6 font-serif-display text-4xl text-[#F6F0E6]">{value}</div>
    {sub && <div className="mt-1 text-xs text-[#D9D9D9]/70">{sub}</div>}
  </div>
);

const Dashboard = () => {
  const [o, setO] = useState(null);
  const [m, setM] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/admin/overview"),
      api.get("/admin/donations/metrics"),
    ]).then(([oRes, mRes]) => {
      setO(oRes.data); setM(mRes.data);
    }).catch(() => {});
  }, []);

  if (!o || !m) return <div className="text-[#D9D9D9]/60" data-testid="metrics-loading">Loading…</div>;

  return (
    <div className="space-y-10" data-testid="admin-dashboard">
      <PageHeader
        overline="Overview"
        title={<>The <span className="italic gold-text">altar</span>, at a glance.</>}
        subtitle="Live data from MongoDB · Stripe · Resend · OneSignal · Google Calendar."
      >
        <div className="text-xs text-[#D9D9D9]/60">As of {fmtDateTime(new Date().toISOString())}</div>
      </PageHeader>

      {/* Drive sync health — visible the moment you sign in */}
      <SyncHealthBadge />

      {/* Donations spotlight */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="bento-card !p-7 md:col-span-2" data-testid="card-this-month">
          <span className="glow-blob" />
          <div className="overline mb-3">Donations · this month</div>
          <div className="font-serif-display text-6xl gold-text leading-none">{fmtUSD(m.month.total)}</div>
          <div className="mt-3 text-sm text-[#D9D9D9]/70">{m.month.count} gift{m.month.count === 1 ? "" : "s"} · YTD {fmtUSD(m.ytd.total)} · All-time {fmtUSD(m.all_time.total)}</div>
          <div className="mt-6 border-t border-[#C9A44C]/15 pt-5 grid grid-cols-4 gap-3 text-center">
            <div><div className="text-[10px] tracking-[0.22em] uppercase text-[#C9A44C]">Today</div><div className="font-serif-display text-2xl text-[#F6F0E6] mt-1">{fmtUSD(m.today.total)}</div></div>
            <div><div className="text-[10px] tracking-[0.22em] uppercase text-[#C9A44C]">This week</div><div className="font-serif-display text-2xl text-[#F6F0E6] mt-1">{fmtUSD(m.week.total)}</div></div>
            <div><div className="text-[10px] tracking-[0.22em] uppercase text-[#C9A44C]">Donors</div><div className="font-serif-display text-2xl text-[#F6F0E6] mt-1">{m.donor_count}</div></div>
            <div><div className="text-[10px] tracking-[0.22em] uppercase text-[#C9A44C]">Avg gift</div><div className="font-serif-display text-2xl text-[#F6F0E6] mt-1">{fmtUSD(m.average_gift)}</div></div>
          </div>
        </div>
        <div className="space-y-5">
          <StatCard testid="card-prayers" label="Prayer · 30d" value={o.new_prayer_requests_30d} sub="New prayer requests" icon={Flame} accent="#F28C18" />
          <StatCard testid="card-signups" label="Members · 30d" value={o.new_members_30d} sub="New ministry signups" icon={Bell} />
        </div>
      </section>

      {/* Engagement row */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-5">
        <StatCard testid="card-recordings" label="Latest displayed" value={o.latest_services_count} sub="Cap: 5 services" icon={Headphones} />
        <StatCard testid="card-review" label="Need review" value={o.ready_for_review} sub="Recordings awaiting approval" icon={AlertTriangle} accent={o.ready_for_review > 0 ? "#F28C18" : "#C9A44C"} />
        <StatCard testid="card-donors" label="Unique donors" value={m.donor_count} sub="All-time" icon={Users} />
        <StatCard testid="card-pending" label="Manual pending" value={o.manual_pending_donations} sub="Donations to reconcile" icon={HandHelping} accent={o.manual_pending_donations > 0 ? "#F28C18" : "#C9A44C"} />
      </section>

      {/* Today's Focus + Last sync */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="bento-card !p-7 lg:col-span-7" data-testid="todays-focus">
          <span className="glow-blob" />
          <div className="overline mb-1">Today&rsquo;s Focus</div>
          <h2 className="font-serif-display text-2xl text-[#F6F0E6] mb-5">What needs ministry leadership attention</h2>
          <ul className="divide-y divide-[#C9A44C]/10">
            {o.todays_focus.map((f) => (
              <li key={f.to}>
                <Link to={f.to} className="flex items-center justify-between py-3 group">
                  <div className="flex items-center gap-3">
                    <span className="h-2 w-2 rounded-full bg-[#C9A44C]" />
                    <span className="text-sm text-[#F6F0E6] group-hover:text-[#C9A44C] transition-colors">{f.label}</span>
                  </div>
                  <ArrowRight className="h-4 w-4 text-[#C9A44C]/60 group-hover:text-[#C9A44C]" />
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="bento-card !p-7 lg:col-span-5" data-testid="last-sync">
          <span className="glow-blob" />
          <div className="overline mb-1">Google Drive · Last Sync</div>
          <h2 className="font-serif-display text-2xl text-[#F6F0E6] mb-4">Media pipeline</h2>
          {o.last_drive_sync ? (
            <div className="space-y-2 text-sm text-[#D9D9D9]">
              <div className="flex justify-between"><span className="text-[#D9D9D9]/60">Type</span><span>{o.last_drive_sync.sync_type}</span></div>
              <div className="flex justify-between"><span className="text-[#D9D9D9]/60">Status</span><span>{(o.last_drive_sync.status || "").replace(/_/g, " ")}</span></div>
              <div className="flex justify-between"><span className="text-[#D9D9D9]/60">Started</span><span>{fmtDateTime(o.last_drive_sync.started_at)}</span></div>
              <div className="flex justify-between"><span className="text-[#D9D9D9]/60">Files</span><span>{o.last_drive_sync.files_imported || 0} imported / {o.last_drive_sync.files_found || 0} found</span></div>
            </div>
          ) : (
            <p className="text-sm text-[#D9D9D9]/70">No syncs have run yet. Configure Google Drive in Settings, then click &ldquo;Sync Now&rdquo; on the Media Library page.</p>
          )}
          <div className="mt-5"><Link to="/admin/media-library" className="btn-ghost !py-2 !px-4 !text-xs"><Calendar className="h-3.5 w-3.5" /> Open Media Library</Link></div>
        </div>
      </section>

      {/* Platform breakdown */}
      <section className="bento-card !p-7" data-testid="platform-breakdown">
        <span className="glow-blob" />
        <div className="overline mb-1">By Platform · This Month</div>
        <h2 className="font-serif-display text-2xl text-[#F6F0E6] mb-5">Where the gifts came from</h2>
        {m.by_platform_month.length === 0 ? (
          <p className="text-sm text-[#D9D9D9]/70">No donations recorded yet this month. As gifts come in (via Stripe webhooks or Manual Donation Entry), they appear here.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {m.by_platform_month.map((row) => (
              <div key={row.platform} className="border border-[#C9A44C]/15 rounded-xl px-3 py-4">
                <div className="text-xs uppercase tracking-wider text-[#C9A44C]">{row.platform}</div>
                <div className="font-serif-display text-2xl text-[#F6F0E6] mt-1">{fmtUSD(row.total)}</div>
                <div className="text-[11px] text-[#D9D9D9]/60 mt-0.5">{row.count} gift{row.count === 1 ? "" : "s"}</div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Dashboard;
